/**
 * Single source of truth for every user-facing string in the bulk-alerts UI
 * (KIR-3252) — the selection-bar summaries, the cross-page affordances, the
 * state-aware action-button labels, and the confirmation-modal copy.
 *
 * Centralized + pure (no React) so the exact singular/plural, filter-aware, and
 * mixed-state wording from the ticket is exhaustively unit-testable. The action
 * labels are derived from the backend eligibility counts
 * (`BulkAlertEligibilityResponse`).
 */
import type {
  BulkAlertAction,
  BulkAlertEligibilityResponse,
} from '@kirin/api-client/contracts/alerts';

// ---------------------------------------------------------------------------
// Grammar helpers
// ---------------------------------------------------------------------------

/** Lowercase noun for prose ("1 alert" / "5 alerts"). */
export function alertsNoun(count: number): string {
  return count === 1 ? 'alert' : 'alerts';
}

/** Title-case noun for button labels ("Mute Alert" / "Mute Alerts"). */
export function alertsLabel(count: number): string {
  return count === 1 ? 'Alert' : 'Alerts';
}

function filterNoun(filterCount: number): string {
  return filterCount === 1 ? 'filter' : 'filters';
}

function thisThese(filterCount: number): string {
  return filterCount === 1 ? 'this' : 'these';
}

function isAre(count: number): string {
  return count === 1 ? 'is' : 'are';
}

// ---------------------------------------------------------------------------
// Selection-bar summaries
// ---------------------------------------------------------------------------

/**
 * Page-level selection summary: "X alert/s on this page selected." — or, when
 * any filter is applied, "X alert/s matching filter/s on this page selected."
 * Selection is per-page (it clears on pagination, per ticket FE#4 / flow#6), so
 * the "on this page" wording is accurate. `filterCount` is the number of active
 * column filters (0 ⇒ no filter wording). Pass ≥1 when only a search term is
 * active so the filter-aware variant is used.
 */
export function selectionSummary(args: {
  count: number;
  filterCount: number;
}): string {
  const { count, filterCount } = args;
  const noun = alertsNoun(count);
  if (filterCount > 0) {
    return `${count} ${noun} matching ${filterNoun(filterCount)} on this page selected.`;
  }
  return `${count} ${noun} on this page selected.`;
}

/**
 * The "select all across pages" affordance shown when the whole current page is
 * selected and more pages exist.
 */
export function selectAllAcrossPagesLabel(args: {
  total: number;
  pageCount: number;
  filterCount: number;
}): string {
  const { total, pageCount, filterCount } = args;
  const noun = alertsNoun(total);
  if (filterCount > 0) {
    return `Select all ${total} ${noun} matching ${thisThese(filterCount)} ${filterNoun(filterCount)} across ${pageCount} pages`;
  }
  return `Select all ${total} ${noun} across ${pageCount} pages`;
}

/**
 * Summary shown once the user has chosen "select all across pages".
 * "Selected all X alerts across Y pages." (filter-aware variant included).
 */
export function selectedAllMatchingSummary(args: {
  total: number;
  pageCount: number;
  filterCount: number;
}): string {
  const { total, pageCount, filterCount } = args;
  const noun = alertsNoun(total);
  if (filterCount > 0) {
    return `Selected all ${total} ${noun} matching ${thisThese(filterCount)} ${filterNoun(filterCount)} across ${pageCount} pages.`;
  }
  return `Selected all ${total} ${noun} across ${pageCount} pages.`;
}

/**
 * Select-all-mode summary WHEN some alerts are excluded — keeps the cross-page
 * context (the count is total − excluded). "X alert/s selected across Y pages."
 * (filter-aware variant included).
 */
export function selectedAcrossPagesSummary(args: {
  count: number;
  pageCount: number;
  filterCount: number;
}): string {
  const { count, pageCount, filterCount } = args;
  const noun = alertsNoun(count);
  if (filterCount > 0) {
    return `${count} ${noun} matching ${filterNoun(filterCount)} selected across ${pageCount} pages.`;
  }
  return `${count} ${noun} selected across ${pageCount} pages.`;
}

// ---------------------------------------------------------------------------
// State-aware action labels (from eligibility counts)
// ---------------------------------------------------------------------------

export interface BulkBarAction {
  action: BulkAlertAction;
  label: string;
  /** Alerts eligible for this action (the count the modal will confirm). */
  eligibleCount: number;
}

/**
 * Mute/unmute buttons to show. All-unmuted → only "Mute Alert/s" (no count);
 * all-muted → only "Unmute Alert/s"; mixed → both, each with its count. An
 * action with zero eligible alerts is omitted.
 */
export function muteUnmuteActions(
  elig: BulkAlertEligibilityResponse
): BulkBarAction[] {
  const mute = elig.eligibleForMute;
  const unmute = elig.eligibleForUnmute;
  const mixed = mute > 0 && unmute > 0;
  const actions: BulkBarAction[] = [];
  if (mute > 0) {
    actions.push({
      action: 'mute',
      eligibleCount: mute,
      // Mixed → count + singular/plural ("Mute 3 Alerts"); single-state → no
      // count, singular/plural noun ("Mute Alert" / "Mute Alerts") per ticket.
      label: mixed
        ? `Mute ${mute} ${alertsLabel(mute)}`
        : `Mute ${alertsLabel(mute)}`,
    });
  }
  if (unmute > 0) {
    actions.push({
      action: 'unmute',
      eligibleCount: unmute,
      label: mixed
        ? `Unmute ${unmute} ${alertsLabel(unmute)}`
        : `Unmute ${alertsLabel(unmute)}`,
    });
  }
  return actions;
}

export interface ExclusionActionsResult {
  actions: BulkBarAction[];
  /** "Exclusion is not available for X selected alert/s" when any are ineligible. */
  ineligibleNote?: string;
}

/**
 * Create/remove-exclusion buttons + the ineligibility note. None-excluded →
 * only "Create Exclusion for Alert/s"; all-excluded → only "Remove Exclusion
 * for Alert/s"; mixed → both with counts. Zero-eligible actions omitted.
 */
export function exclusionActions(
  elig: BulkAlertEligibilityResponse
): ExclusionActionsResult {
  const create = elig.eligibleForExclusion;
  const remove = elig.eligibleForRemoveExclusion;
  const mixed = create > 0 && remove > 0;
  const actions: BulkBarAction[] = [];
  if (create > 0) {
    actions.push({
      action: 'exclude',
      eligibleCount: create,
      label: mixed
        ? `Create Exclusion for ${create} ${alertsLabel(create)}`
        : `Create Exclusion for ${alertsLabel(create)}`,
    });
  }
  if (remove > 0) {
    actions.push({
      action: 'remove-exclusion',
      eligibleCount: remove,
      label: mixed
        ? `Remove Exclusion from ${remove} ${alertsLabel(remove)}`
        : `Remove Exclusion for ${alertsLabel(remove)}`,
    });
  }
  const ineligibleNote =
    elig.exclusionIneligible > 0
      ? `Exclusion is not available for ${elig.exclusionIneligible} selected ${alertsNoun(elig.exclusionIneligible)}`
      : undefined;
  return { actions, ineligibleNote };
}

// ---------------------------------------------------------------------------
// Confirmation-modal copy
// ---------------------------------------------------------------------------

export interface BulkModalCopy {
  title: string;
  /** Bold sub-heading (exclusion warning header) — present only for exclude. */
  subtitle?: string;
  /** Impact paragraph (mute silence note / exclusion warning body). */
  body?: string;
  /** "[eligible] selected alert/s will be …" line. */
  countLine: string;
  /** "[already] selected alert/s are/is already … and will not change." Omitted when 0. */
  alreadyLine?: string;
  confirmLabel: string;
  /** Destructive (red) confirm button — exclusion only. */
  destructive: boolean;
  /** Whether the modal shows the duration picker (mute / create-exclusion). */
  needsDuration: boolean;
}

/**
 * Copy for the per-action confirmation modal, matching the ticket's modal
 * table. `eligibleCount` drives singular/plural; `alreadyCount` (already in the
 * target state) renders the "will not change" line, omitted when zero.
 */
export function bulkModalCopy(
  action: BulkAlertAction,
  counts: { eligibleCount: number; alreadyCount: number }
): BulkModalCopy {
  const e = counts.eligibleCount;
  const a = counts.alreadyCount;
  const single = e === 1;

  switch (action) {
    case 'mute':
      return {
        title: single ? 'Mute Alert?' : `Mute ${e} Alerts?`,
        body: single
          ? 'Silence this alert for your workspace, detection and enforcement continue.'
          : 'Silence these alerts for your workspace. Detection and enforcement continue.',
        countLine: single
          ? '1 selected alert will be muted.'
          : `${e} selected alerts will be muted.`,
        alreadyLine:
          a > 0
            ? `${a} selected ${alertsNoun(a)} ${isAre(a)} already muted and will not change.`
            : undefined,
        confirmLabel: single ? 'Mute Alert' : 'Mute Alerts',
        destructive: false,
        needsDuration: true,
      };
    case 'unmute':
      return {
        title: single ? 'Unmute Alert?' : `Unmute ${e} Alerts?`,
        countLine: single
          ? '1 selected alert will be unmuted.'
          : `${e} selected alerts will be unmuted.`,
        alreadyLine:
          a > 0
            ? `${a} selected ${alertsNoun(a)} ${isAre(a)} already unmuted and will not change.`
            : undefined,
        confirmLabel: single ? 'Unmute Alert' : 'Unmute Alerts',
        destructive: false,
        needsDuration: false,
      };
    case 'exclude':
      return {
        title: single
          ? 'Create Exclusion - Warning'
          : `Create ${e} Exclusions - Warning`,
        subtitle: single
          ? 'Creating an exclusion will disable prevention for activity matching this alert.'
          : 'Creating an exclusion will disable prevention for activity matching these alerts.',
        body: single
          ? 'Activity matching this pattern will no longer trigger enforcement actions and threats may proceed unblocked and undetected. Create exclusions only for alerts you have fully investigated and confirmed as safe.'
          : 'Activity matching these patterns will no longer trigger enforcement actions and threats may proceed unblocked and undetected. Create exclusions only for alerts you have fully investigated and confirmed as safe.',
        countLine: single
          ? '1 selected alert will be excluded.'
          : `${e} selected alerts will be excluded.`,
        alreadyLine:
          a > 0
            ? `${a} selected ${alertsNoun(a)} ${isAre(a)} already excluded and will not change.`
            : undefined,
        confirmLabel: single ? 'Create Exclusion' : 'Create Exclusions',
        destructive: true,
        needsDuration: true,
      };
    case 'remove-exclusion':
      return {
        title: single
          ? 'Remove Exclusion from Alert?'
          : `Remove Exclusions from ${e} Alerts?`,
        countLine: single
          ? '1 selected alert exclusion will be removed.'
          : `${e} selected alerts exclusions will be removed.`,
        alreadyLine:
          a > 0
            ? a === 1
              ? "1 selected alert already doesn't have exclusion and will not change."
              : `${a} selected alerts already don't have exclusions and will not change.`
            : undefined,
        confirmLabel: single ? 'Remove Exclusion' : 'Remove Exclusions',
        destructive: false,
        needsDuration: false,
      };
  }
}

// ---------------------------------------------------------------------------
// Result toasts (all-or-nothing → single success/failure line, no undo)
// ---------------------------------------------------------------------------

/** Success toast: "X Alert/s muted" / "X Exclusion/s removed", etc. */
export function bulkActionSuccessToast(
  action: BulkAlertAction,
  changedCount: number
): string {
  const n = changedCount;
  switch (action) {
    case 'mute':
      return `${n} ${alertsLabel(n)} muted`;
    case 'unmute':
      return `${n} ${alertsLabel(n)} unmuted`;
    case 'exclude':
      return `${n} ${alertsLabel(n)} excluded`;
    case 'remove-exclusion':
      return `${n} ${n === 1 ? 'Exclusion' : 'Exclusions'} removed`;
  }
}

/** Failure toast: generic per-action message (no per-row reason — all-or-nothing). */
export function bulkActionFailureToast(action: BulkAlertAction): string {
  switch (action) {
    case 'mute':
      return 'Mute action failed';
    case 'unmute':
      return 'Unmute action failed';
    case 'exclude':
      return 'Exclude action failed';
    case 'remove-exclusion':
      return 'Remove exclusion action failed';
  }
}

/**
 * The eligible / already-in-target-state counts for an action, sourced from the
 * eligibility preview — used to parameterize the confirmation modal.
 */
export function modalCountsForAction(
  action: BulkAlertAction,
  elig: BulkAlertEligibilityResponse
): { eligibleCount: number; alreadyCount: number } {
  switch (action) {
    case 'mute':
      return {
        eligibleCount: elig.eligibleForMute,
        alreadyCount: elig.alreadyMuted,
      };
    case 'unmute':
      return {
        eligibleCount: elig.eligibleForUnmute,
        alreadyCount: elig.alreadyUnmuted,
      };
    case 'exclude':
      return {
        eligibleCount: elig.eligibleForExclusion,
        alreadyCount: elig.alreadyExcluded,
      };
    case 'remove-exclusion':
      return {
        eligibleCount: elig.eligibleForRemoveExclusion,
        alreadyCount: elig.withoutExclusion,
      };
  }
}
